import back4 from "@/assets/images/back4.jpg";
import bg_09 from "@/assets/images/bg_09.jpg";
import Footer from "@/components/feature/Footer";
import Navbar from "@/components/feature/Navbar";
import { supabase } from "@/lib/supabase";
import { useState } from "react";

const ContactPage = () => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === "message") {
      if (value.length > 500) return;
      setCharCount(value.length);
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    setSubmitting(true);
    try {
      await supabase.from("contact_submissions").insert({
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        subject: formData.subject || null,
        message: formData.message,
        status: "new",
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const contactMethods = [
    {
      icon: "ri-whatsapp-line",
      title: "WhatsApp",
      value: "+91 8180084435",
      description: "Chat with us for quick responses",
      href: "https://wa.me/+918180084435",
      color: "from-green-400 to-green-500",
    },
    {
      icon: "ri-mail-line",
      title: "Email",
      value: "giftmantra51@gmail.com",
      description: "Send us an email anytime",
      href: "mailto:giftmantra51@gmail.com",
      color: "from-[#3a8cbf] to-[#5ab0d4]",
    },
    {
      icon: "ri-phone-line",
      title: "Phone",
      value: "+91 8180084435",
      description: "Call us during business hours",
      href: "tel:+918180084435",
      color: "from-[#F78FB3] to-[#f5b8cc]",
    },
    {
      icon: "ri-map-pin-line",
      title: "Visit Us",
      value: "Mumbai, Maharashtra",
      description: "Come see our collection in person",
      href: "#",
      color: "from-[#a8c8e8] to-[#f0a8c8]",
    },
  ];

  return (
    <>
      <Navbar />
      <main className="bg-white">
        {/* Hero Section */}
        <section className="relative pt-20 lg:pt-24">
          <div
            className="h-64 lg:h-80 bg-cover bg-center relative"
            style={{
              backgroundImage:
                `url(${bg_09})`,
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60" />
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
              <span className="text-xs font-semibold tracking-[0.3em] uppercase text-white/70 mb-3">Get In Touch</span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                Contact Us
              </h1>
              <p className="text-white/80 text-base mt-4 max-w-xl">
                We&apos;d love to hear from you. Reach out for custom orders, bulk gifting, or just to say hello.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Methods Grid */}
        <section className="py-16 lg:py-20 px-6 lg:px-10 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {contactMethods.map((method, i) => (
                <a
                  key={i}
                  href={method.href}
                  target={method.href.startsWith("http") ? "_blank" : undefined}
                  rel={method.href.startsWith("http") ? "nofollow noopener noreferrer" : undefined}
                  className="group p-6 rounded-2xl border border-gray-100 hover:border-transparent hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer"
                >
                  <div className={`w-14 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br ${method.color} mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <i className={`${method.icon} text-white text-2xl`} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {method.title}
                  </h3>
                  <p className="text-sm font-medium text-transparent bg-clip-text bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a] mb-2">
                    {method.value}
                  </p>
                  <p className="text-sm text-gray-500">{method.description}</p>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form & Info Section */}
        <section className="py-16 lg:py-24 px-6 lg:px-10 bg-gradient-to-br from-[#f0f8ff] via-white to-[#fff0f5]">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Left: Form */}
            <div className="bg-white rounded-3xl border border-gray-100 p-8 lg:p-10">
              <span className="text-xs font-semibold tracking-[0.3em] uppercase text-transparent bg-clip-text bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a]">
                Send Message
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mt-2 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                How Can We Help?
              </h2>

              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 flex items-center justify-center rounded-full bg-gradient-to-br from-[#5FA8D3]/10 to-[#F78FB3]/10 mx-auto mb-4">
                    <i className="ri-check-line text-4xl text-transparent bg-clip-text bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a]" />
                  </div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">Message Sent!</h4>
                  <p className="text-gray-500">Thank you for reaching out. We&apos;ll get back to you within 24 hours.</p>
                </div>
              ) : (
                <form
                  id="contact-form"
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Your name"
                        className="w-full px-4 py-3 rounded-xl bg-gray-50 border-2 border-transparent focus:border-[#3a8cbf] focus:bg-white text-sm text-gray-800 placeholder-gray-400 outline-none transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="your@email.com"
                        className="w-full px-4 py-3 rounded-xl bg-gray-50 border-2 border-transparent focus:border-[#3a8cbf] focus:bg-white text-sm text-gray-800 placeholder-gray-400 outline-none transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+91 99999 99999"
                        className="w-full px-4 py-3 rounded-xl bg-gray-50 border-2 border-transparent focus:border-[#3a8cbf] focus:bg-white text-sm text-gray-800 placeholder-gray-400 outline-none transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Subject *</label>
                      <select
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl bg-gray-50 border-2 border-transparent focus:border-[#3a8cbf] focus:bg-white text-sm text-gray-800 outline-none transition-all cursor-pointer"
                      >
                        <option value="">Select a subject</option>
                        <option value="General Inquiry">General Inquiry</option>
                        <option value="Custom Order">Custom Order</option>
                        <option value="Bulk Gifting">Bulk Gifting</option>
                        <option value="Product Question">Product Question</option>
                        <option value="Feedback">Feedback</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Message *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      maxLength={500}
                      placeholder="Tell us how we can help you..."
                      className="w-full px-4 py-3 rounded-xl bg-gray-50 border-2 border-transparent focus:border-[#3a8cbf] focus:bg-white text-sm text-gray-800 placeholder-gray-400 outline-none transition-all resize-none"
                    />
                    <p className="text-xs text-gray-400 mt-1 text-right">{charCount}/500</p>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting || charCount > 500}
                    className="w-full py-4 rounded-xl bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a] text-white font-semibold text-sm hover:opacity-90 disabled:opacity-60 transition-opacity whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line" />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Right: Store Info & Map */}
            <div className="flex flex-col gap-8">
              {/* Store Image */}
              <div className="relative rounded-3xl overflow-hidden h-64 lg:h-80">
                <img
                  src={back4}
                  alt="Gift Mantra Store"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <h3 className="text-xl font-bold text-white mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Visit Our Store
                  </h3>
                  <p className="text-white/80 text-sm">Experience our collection in person</p>
                </div>
              </div>

              {/* Business Hours */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  <i className="ri-time-line text-transparent bg-clip-text bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a]" />
                  Business Hours
                </h3>
                <div className="space-y-3">
                  {[
                    { day: "Monday - Friday", hours: "10:00 AM - 8:00 PM" },
                    { day: "Saturday", hours: "11:00 AM - 7:00 PM" },
                    { day: "Sunday", hours: "12:00 PM - 6:00 PM" },
                  ].map((schedule, i) => (
                    <div key={i} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                      <span className="text-sm text-gray-600">{schedule.day}</span>
                      <span className="text-sm font-medium text-gray-900">{schedule.hours}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Response Promise */}
              <div className="bg-gradient-to-r from-[#3a8cbf]/5 to-[#e05a8a]/5 rounded-2xl p-6 border border-[#3a8cbf]/10">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br from-[#3a8cbf] to-[#e05a8a] flex-shrink-0">
                    <i className="ri-flashlight-line text-white text-xl" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Fast Response Promise</h4>
                    <p className="text-sm text-gray-600">
                      We aim to respond to all inquiries within 24 hours. For urgent matters, WhatsApp us for instant replies.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 lg:py-20 px-6 lg:px-10 bg-white">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-xs font-semibold tracking-[0.3em] uppercase text-transparent bg-clip-text bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a]">
                Common Questions
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                Frequently Asked Questions
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  q: "Do you offer custom gift packaging?",
                  a: "Yes! We offer premium gift wrapping with personalized messages, ribbons, and custom boxes for all orders.",
                },
                {
                  q: "What is your delivery timeframe?",
                  a: "Standard delivery is 3-5 business days. Express delivery available for urgent orders within 24-48 hours.",
                },
                {
                  q: "Can I place bulk orders for corporate gifting?",
                  a: "Absolutely! We specialize in corporate gifting with custom branding options. Contact us for bulk pricing.",
                },
                {
                  q: "Do you ship internationally?",
                  a: "Currently we ship across India. International shipping coming soon — join our newsletter for updates!",
                },
              ].map((faq, i) => (
                <div key={i} className="p-6 rounded-2xl border border-gray-100 hover:border-[#3a8cbf]/30 transition-colors">
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-start gap-2">
                    <i className="ri-question-line text-[#3a8cbf] mt-0.5" />
                    {faq.q}
                  </h4>
                  <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Social Connect */}
        <section className="py-16 px-6 lg:px-10 bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a]">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Connect With Us
            </h2>
            <p className="text-white/80 mb-8 max-w-xl mx-auto">
              Follow us on social media for daily inspiration, new arrivals, and exclusive offers.
            </p>
            <div className="flex justify-center gap-4">
              {[
                { icon: "ri-instagram-line", href: "https://instagram.com", label: "Instagram" },
                { icon: "ri-facebook-line", href: "https://facebook.com", label: "Facebook" },
                { icon: "ri-pinterest-line", href: "https://pinterest.com", label: "Pinterest" },
                { icon: "ri-youtube-line", href: "https://youtube.com", label: "YouTube" },
              ].map((s, i) => (
                <a
                  key={i}
                  href={s.href}
                  aria-label={s.label}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="w-12 h-12 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white hover:text-[#3a8cbf] transition-all duration-300 cursor-pointer"
                >
                  <i className={`${s.icon} text-xl`} />
                </a>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default ContactPage;